import axios from "axios";
import moment from "moment";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";

import { ICloudDocumentAvailabilityStatus, ICloudDocumentAvailabilityStatusRequest, ICombineDocumentRequest, IDocument } from "../types/document";
import { IShipmentDocument } from "../types/shipmentPacket";
import { promises } from "dns";

export namespace DocumentService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Document.
	 * @param data Document response data from an API.
	 **********/
	export function mapDocument(data: any) {
		let document: IDocument = {
			id: data.id,
			contentType: data.contentType,
			documentTypeId: data.documentTypeId,
			documentTypeName: data.documentTypeName,
			entityId: data.entityId,
			entityName: data.entityName,
			entityTypeId: data.entityTypeId,
			entityTypeName: data.entityTypeName,
			expirationDate: data.expirationDate,
			file: data.file,
			location: data.location,
			name: data.name,
			uploadDate: data.uploadDate,
			status: data.expirationDate ? (moment(data.expirationDate).diff(Date.now()) >= 0 ? "Active" : "Expired") : "Active",
			isSensitive: data.isSensitive,
		}

		return document;
	}

	/**********
	 * Convert an array of api response objects to an array of Documents.
	 * @param data Document data from an API response.
	 **********/
	export function mapDocuments(data: any) {
		let documents: IDocument[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				documents.push(mapDocument(item));
			})
		}

		return documents;
	}

	/**********
	 * Gets all documents.
	 **********/
	export async function getDocuments() {
		const apiResponse = axios.get(
			apiConfig.default + "/document/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Get a single document.
	 * @param document The document id.
	 **********/
	export async function getDocument(id: number): Promise<IDocument> {
		const metaResponse = await axios.get(
			apiConfig.default + "/document/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					//'responseType': 'application/pdf'
				},
			}
		);

		return metaResponse.data;
	}

	/**********
	 * Check if the contents of the provided documents exist in Azure.
	 * @param documentIds A list of document IDs to check.
	 **********/
	export async function checkExistsInAzure(documentIds: number[]): Promise<ICloudDocumentAvailabilityStatus[]> {
		const request: ICloudDocumentAvailabilityStatusRequest = { documentIds: documentIds };

		const apiResponse = await axios.post(			
			apiConfig.default + "/document/exists",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
				},
			}
		);

		return apiResponse.data;
	}

	/**********
	 * Add a new document.
	 * @param document The document information.
	 **********/
	export async function addDocument(document: IDocument) {
		const apiResponse = axios.put(
			apiConfig.default + "/document",
			document,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Delete a document.
	 * @param id The document ID.
	 **********/
	export async function deleteDocument(id: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/document/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	};

	/**********
	 * Combines documents of provided IDs into a single PDF document.
	 * @param request The request containing document IDs to combine.
	 * @return A promise with the API response as an IGeneratedDocument containing a PDF file.
	 **********/
	export async function generateCombinedDocument(documentIds: number[], outputFilename?: string) {
		const request: ICombineDocumentRequest = { documentIds: documentIds, outputFilename: outputFilename };
		const apiResponse = axios.post(
			apiConfig.default + "/document/combine",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Collects documents of provided IDs and compresses them into a ZIP archive.
	 * @param request The request containing document IDs to gather.
	 * @return A promise with the API response as an IGeneratedDocument containing a ZIP file.
	 **********/
	export async function zipShipmentPacketDocument(documentIds: number[], outputFilename?: string) {
		const request: ICombineDocumentRequest = { documentIds: documentIds, outputFilename: outputFilename };
		const apiResponse = axios.post(
			apiConfig.default + "/document/zipshipmentpacketdocuments",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Exports the contents of the Combined Document Contents table to an Excel file.
	 * @param documents The shipment packet document information to export.
	 * @return A promise with the API response as an IGeneratedDocument containing an Excel file.
	 **********/
	export async function exportShipmentPacketTableToExcel(documents: IShipmentDocument[], outputFilename?: string) {		
		const apiResponse = axios.post(
			apiConfig.default + "/document/exportshipmentpackettabletoexcel",
			documents,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}
